/**
 * SimulationAudioControls
 *
 * Provides voice selection, autoplay, and audio export for
 * completed Advanced Simulation runs. Admin-only.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ConversationAutoplay from '../../therapist/ConversationAutoplay';
import SimulationAudioExportDialog from './SimulationAudioExportDialog';
import { getElevenLabsVoices } from '../../../services/api';

const SimulationAudioControls = ({ turns, runId, token }) => {
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [voiceError, setVoiceError] = useState(null);
  const [therapistVoiceId, setTherapistVoiceId] = useState('');
  const [clientVoiceId, setClientVoiceId] = useState('');
  const [showAutoplay, setShowAutoplay] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Fetch available voices directly from ElevenLabs (same pattern used throughout the app)
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        setLoadingVoices(true);
        setVoiceError(null);
        // getElevenLabsVoices returns raw ElevenLabs voice objects with voice_id, name, category, labels, etc.
        const voiceList = await getElevenLabsVoices();
        setVoices(voiceList);

        // Set defaults if available
        if (voiceList.length > 0) {
          const defaultTherapist = voiceList.find(v =>
            v.name?.toLowerCase().includes('rachel') ||
            v.name?.toLowerCase().includes('sarah')
          ) || voiceList[0];
          const defaultClient = voiceList.find(v =>
            v.name?.toLowerCase().includes('adam') ||
            v.name?.toLowerCase().includes('josh')
          ) || voiceList[Math.min(1, voiceList.length - 1)];

          setTherapistVoiceId(defaultTherapist.voice_id || '');
          setClientVoiceId(defaultClient.voice_id || '');
        }
      } catch (err) {
        console.error('Failed to fetch ElevenLabs voices:', err);
        setVoiceError('Failed to load available voices. Check ElevenLabs API key configuration.');
      } finally {
        setLoadingVoices(false);
      }
    };

    fetchVoices();
  }, []);

  // Convert simulation turns to message format expected by ConversationAutoplay
  const convertedMessages = useMemo(() => {
    if (!turns) return [];
    return turns.map((turn, idx) => {
      let senderRole;
      switch (turn.actor) {
        case 'therapist': senderRole = 'ai'; break;
        case 'client': senderRole = 'client'; break;
        case 'system': senderRole = 'system'; break;
        default: senderRole = 'ai';
      }
      return {
        _id: `sim-turn-${idx}`,
        text: turn.content,
        senderRole,
        timestamp: turn.timestamp || new Date()
      };
    });
  }, [turns]);

  // Build synthetic platformSettings so getVoiceForMessage resolves to admin-chosen voices
  const syntheticPlatformSettings = useMemo(() => ({
    voiceSettings: {
      defaultAITherapistVoiceId: therapistVoiceId,
      defaultClientVoiceId: clientVoiceId,
      defaultHumanTherapistVoiceId: therapistVoiceId,
      defaultSystemVoiceId: therapistVoiceId
    }
  }), [therapistVoiceId, clientVoiceId]);

  const canPlay = therapistVoiceId && clientVoiceId && convertedMessages.length > 0;

  if (loadingVoices) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading voices...</Typography>
      </Box>
    );
  }

  if (voiceError) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {voiceError}
      </Alert>
    );
  }

  const getVoiceLabel = (voice) => {
    return `${voice.name || voice.voice_id}${voice.category ? ` (${voice.category})` : ''}`;
  };

  const getVoiceValue = (voice) => voice.voice_id;

  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
        flexWrap: 'wrap'
      }}>
        <VolumeUpIcon color="action" />
        <Typography variant="subtitle2" sx={{ mr: 1 }}>Audio:</Typography>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Therapist Voice</InputLabel>
          <Select
            value={therapistVoiceId}
            onChange={(e) => setTherapistVoiceId(e.target.value)}
            label="Therapist Voice"
          >
            {voices.map((voice) => (
              <MenuItem key={getVoiceValue(voice)} value={getVoiceValue(voice)}>
                {getVoiceLabel(voice)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Client Voice</InputLabel>
          <Select
            value={clientVoiceId}
            onChange={(e) => setClientVoiceId(e.target.value)}
            label="Client Voice"
          >
            {voices.map((voice) => (
              <MenuItem key={getVoiceValue(voice)} value={getVoiceValue(voice)}>
                {getVoiceLabel(voice)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => setShowAutoplay(true)}
          disabled={!canPlay}
        >
          Play Conversation
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setShowExportDialog(true)}
          disabled={!canPlay}
        >
          Export Audio
        </Button>
      </Box>

      {/* Autoplay overlay */}
      {showAutoplay && (
        <ConversationAutoplay
          messages={convertedMessages}
          userSettings={null}
          platformSettings={syntheticPlatformSettings}
          therapistSettings={null}
          clientId={null}
          conversationId={null}
          hideExportOption={true}
          token={token}
          onClose={() => setShowAutoplay(false)}
        />
      )}

      {/* Export dialog */}
      {showExportDialog && (
        <SimulationAudioExportDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          runId={runId}
          messageCount={convertedMessages.length}
          therapistVoiceId={therapistVoiceId}
          clientVoiceId={clientVoiceId}
          token={token}
        />
      )}
    </>
  );
};

export default SimulationAudioControls;
